import { Button, TextInput, Switch, Group, Center, Text } from "@mantine/core";
import { IconSearch, IconPlus } from "@tabler/icons-react";
import { useEffect, useState, useMemo } from "react";
import { AddUserForm, EditUserForm, ViewUserForm } from "./forms/modals/UserForms";
import { UserType } from "../../types/User";
import { getAllUsers } from "../../services/UserService";
import { Role } from "../../types/Role";
import { SortableTable, ColumnDef } from "../SortableTable";
import useTransientStore from "../../data/transcientStore";
import styles from './UsersTable.module.css';
import { useTranslation } from "../../hooks/useTranslation";


interface UsersTableProps {
    userData: UserType[],
    roles: Role[],
    refreshUsers: () => void;
    refreshAddressSystems: () => void;
}

export default function UsersTable({ userData, roles, refreshUsers, refreshAddressSystems }: UsersTableProps ) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 307, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    // const [activePage, setPage] = useState(1);
    const [userSearch, setUserSearch] = useState<string>('');
    // const [searchError, setSearchError] = useState<boolean>(false);
    const [addUserModalOpened, setAddUserModalOpened] = useState(false);
    const [editUserModalOpened, setEditUserModalOpened] = useState(false);
    const [viewUserModalOpened, setViewUserModalOpened] = useState(false);
    const [highlightedRowId, setHighlightedRowId] = useState<number | null>(null);
    const organisations = useTransientStore((state) => state.allOrganisations);
    const [selectedID, setSelectedID] = useState<number>(-1);
    const [allUsers, setAllUsers] = useState<UserType[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserType>({
        id: 0,
        username: '',
        role: {
            id: 0,
            name: '',
            description: ''
        },
        addresses: [],
        organisationId: 0,
        email: '',
        name: '',
        active: 0
    });
    const [statusFilter, setStatusFilter] = useState<1 | 0>(1);
    const pageSize = 100;

    const handleUserSuccess = () => {
        refreshUsers();
        refreshAddressSystems();
    };


    useEffect(() => {
        async function fetchAllUsers() {
            const users = await getAllUsers();
            setAllUsers(users);
        }
        fetchAllUsers();
    }, []);

    useEffect(() => {
        const user = userData.find((user) => user.id === selectedID)
        if (user) {
            setSelectedUser(user);
        }
    }, [selectedID, userData]);

    // useEffect(() => {
    //     function updatePageSize() {
    //         const availableHeight = window.innerHeight - 300;
    //         const rows = Math.max(3, Math.floor(availableHeight / 48));
    //         setPageSize(rows);
    //     }
    //     updatePageSize();
    //     window.addEventListener('resize', updatePageSize);
    //     return () => window.removeEventListener('resize', updatePageSize);
    // }, []);

    useEffect(() => {
        setUserSearch('');
    }, []);

    const filteredUsers = useMemo(() => {
        const filtered = userData
            .filter((item) => item.active === statusFilter)
            .map((user) => ({
                ...user,
                organisationName: organisations.find(org => org.id === user.organisationId)?.name || ''
            }))
            .filter((item) => {
                const searchTerm = userSearch.toLowerCase().trim();
                if (!searchTerm) return true;
                
                // Search across multiple fields
                const username = item.username.toLowerCase();
                const email = (item.email || '').toLowerCase();
                const roleName = item.role.name.toLowerCase();
                const organisationName = item.organisationName.toLowerCase();
                
                return username.includes(searchTerm) || 
                       email.includes(searchTerm) || 
                       roleName.includes(searchTerm) || 
                       organisationName.includes(searchTerm);
            });

        if (userSearch.toLowerCase().trim()) {
            const searchTerm = userSearch.toLowerCase().trim();
            
            return filtered.sort((a, b) => {
                const aUsername = a.username.toLowerCase();
                const bUsername = b.username.toLowerCase();
                const aEmail = (a.email || '').toLowerCase();
                const bEmail = (b.email || '').toLowerCase();
                const aRole = a.role.name.toLowerCase();
                const bRole = b.role.name.toLowerCase();
                const aOrg = a.organisationName.toLowerCase();
                const bOrg = b.organisationName.toLowerCase();
                
                // Exact match priority
                const aExactUsername = aUsername === searchTerm;
                const bExactUsername = bUsername === searchTerm;
                if (aExactUsername && !bExactUsername) return -1;
                if (!aExactUsername && bExactUsername) return 1;
                
                const aExactEmail = aEmail === searchTerm;
                const bExactEmail = bEmail === searchTerm;
                if (aExactEmail && !bExactEmail) return -1;
                if (!aExactEmail && bExactEmail) return 1;
                
                const aExactRole = aRole === searchTerm;
                const bExactRole = bRole === searchTerm;
                if (aExactRole && !bExactRole) return -1;
                if (!aExactRole && bExactRole) return 1;
                
                const aExactOrg = aOrg === searchTerm;
                const bExactOrg = bOrg === searchTerm;
                if (aExactOrg && !bExactOrg) return -1;
                if (!aExactOrg && bExactOrg) return 1;
                
                // Pefix match priority
                const aStartsUsername = aUsername.startsWith(searchTerm);
                const bStartsUsername = bUsername.startsWith(searchTerm);
                if (aStartsUsername && !bStartsUsername) return -1;
                if (!aStartsUsername && bStartsUsername) return 1;
                
                const aStartsEmail = aEmail.startsWith(searchTerm);
                const bStartsEmail = bEmail.startsWith(searchTerm);
                if (aStartsEmail && !bStartsEmail) return -1;
                if (!aStartsEmail && bStartsEmail) return 1;
                
                const aStartsRole = aRole.startsWith(searchTerm);
                const bStartsRole = bRole.startsWith(searchTerm);
                if (aStartsRole && !bStartsRole) return -1;
                if (!aStartsRole && bStartsRole) return 1;
                
                const aStartsOrg = aOrg.startsWith(searchTerm);
                const bStartsOrg = bOrg.startsWith(searchTerm);
                if (aStartsOrg && !bStartsOrg) return -1;
                if (!aStartsOrg && bStartsOrg) return 1;
                
                // Default alphabetical sort
                const usernameCompare = aUsername.localeCompare(bUsername);
                if (usernameCompare !== 0) return usernameCompare;
                
                const emailCompare = aEmail.localeCompare(bEmail);
                if (emailCompare !== 0) return emailCompare;
                
                const roleCompare = aRole.localeCompare(bRole);
                if (roleCompare !== 0) return roleCompare;
                
                return aOrg.localeCompare(bOrg);
            });
        }
        return filtered;
    }, [userData, statusFilter, organisations, userSearch]);

    const columns: ColumnDef<UserType & { organisationName: string }>[] = [
        { header: getTranslation("LABEL_USERS_TABLE_USERNAME") || "Username", accessor: 'username', sortable: true, width: 180 },
        {
            header: getTranslation("LABEL_USERS_TABLE_ORGANISATION") || "Organisation", accessor: 'organisationName', sortable: true, width: 200,
            render: (row) => {
                const organisation = organisations.find(org => org.id === row.organisationId);
                return organisation?.name || '-';
            }
        },
        { header: getTranslation("LABEL_USERS_TABLE_ROLE") || "Role", accessor: 'role.name', sortable: true, render: (row) => row.role.name, width: 150 },
        { header: getTranslation("LABEL_USERS_TABLE_EMAIL") || "Email", accessor: 'email', sortable: true, width: 250 },
    ];

    return (
        <div className={styles.container}>
            <AddUserForm opened={addUserModalOpened} open={() => setAddUserModalOpened(true)} close={() => setAddUserModalOpened(false)} roles={roles} allUsers={allUsers} onSuccess={handleUserSuccess}/>
            <EditUserForm opened={editUserModalOpened} open={() => setEditUserModalOpened(true)} close={() => { setEditUserModalOpened(false); setHighlightedRowId(null); }} selectedUser={selectedUser} roles={roles} allUsers={allUsers} onSuccess={handleUserSuccess}/>
            <ViewUserForm opened={viewUserModalOpened} close={() => { setViewUserModalOpened(false); setHighlightedRowId(null); }} selectedUser={selectedUser} />

            <Group mb="md" justify="space-between" align="center" className={styles.headerGroup}>
                <Switch
                    // label={getTranslation("LABEL_ARCHIVED") || "Archived"}
                    checked={statusFilter === 0}
                    onChange={(event) => setStatusFilter(event.currentTarget.checked ? 0 : 1)}
                    styles={(theme) => ({
                        track: {
                            backgroundColor: statusFilter === 1 ? 'gray' : undefined,
                            borderColor: statusFilter === 1 ? 'gray' : undefined,
                        }
                    })}
                />
                <Text fw={500} fz="sm">{getTranslation("LABEL_USERS_TABLE_ARCHIVED") || "Archived"}</Text>
                <TextInput
                    value={userSearch}
                    onChange={(event) => { setUserSearch(event.currentTarget.value) }}
                    placeholder={getTranslation("PLACEHOLDER_SEARCH_USERNAMES_EMAILS_ROLES_OR_ORGANISATIONS") || "Search usernames, emails, roles, or organisations..."}
                    leftSection={<IconSearch size={18} color="#009DF0" />}
                    className={styles.searchInput}
                />
                <Button onClick={() => setAddUserModalOpened(true)} rightSection={<IconPlus size={16} />}>
                    {getTranslation("LABEL_USERS_TABLE_ADD_USER") || "Add User"}
                </Button>
            </Group>

            <SortableTable
                data={filteredUsers}
                columns={columns}
                pageSize={pageSize}
                rowKey={(row) => row.id}
                rowStyle={(row) => 
                    highlightedRowId === row.id 
                        ? { backgroundColor: '#e3f0ff' }
                        : {}
                }
                actionColumns={[
                    {
                        label: getTranslation("LABEL_USERS_TABLE_ADDRESS_SYSTEMS") || "Address Systems",
                        width: 140,
                        render: (row) => (
                            <Button onClick={() => { 
                                setSelectedID(row.id); 
                                setHighlightedRowId(row.id);
                                setViewUserModalOpened(true); 
                            }} 
                            w={60} 
                            disabled={row.addresses.length === 0}>
                                {row.addresses.length}
                            </Button>
                        )
                    },
                    {
                        label: getTranslation("LABEL_USERS_TABLE_EDIT") || "Edit",
                        width: 80,
                        render: (row) => (
                            <Button onClick={() => { 
                                setSelectedID(row.id); 
                                setHighlightedRowId(row.id);
                                setEditUserModalOpened(true); 
                            }}>
                                {getTranslation("LABEL_USERS_TABLE_EDIT") || "Edit"}
                            </Button>
                        )
                    }
                ]}
            />
            {filteredUsers.length === 0 && (
                <Center mt="xl">
                    <Text>{getTranslation("LABEL_USERS_TABLE_NO_RECORDS") || "No records."}</Text>
                </Center>
            )}
        </div>
    )
}